import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Form,
  Label,
  Input,
  Button,
  Spinner,
} from "reactstrap";

import axios from "../../../utills/api";
import { toast } from "react-toastify";

const CreateReservationModal = ({
  isOpen,
  toggle,
  onSuccess,
}) => {
  const [loading, setLoading] =
    useState(false);

  const [loadingRooms, setLoadingRooms] = useState(false);
  const [fullPayment, setFullPayment] = useState(false);

  const [roomTypes, setRoomTypes] =
    useState([]);

  const [rooms, setRooms] =
    useState([]);

  const [priceSummary, setPriceSummary] =
    useState(null);

  const initialState = {
    // Guest
    name: "",
    mobile: "",
    email: "",
    address: "",
    nationality: "",
    idProofType: "",
    idProofNumber: "",

    // Stay
    checkIn: "",
    checkOut: "",

    adults: 1,
    children: 0,
    numberOfGuests: 1,

    roomTypeId: "",
    assignedRoom: "",

    // Extra Bed
    extraBedRequired: false,
    extraBedCount: 0,

    // Booking
    source: "MANUAL",
    syncSource: "MANUAL",
    channel: "DIRECT",

    specialRequest: "",
    notes: "",

    // Payment
    discountAmount: 0,
    advanceAmount: 0,
    paymentMode: "offline",
  };

  const [form, setForm] =
    useState(initialState);

  //------------------------------------------------
  // Change Handler
  //------------------------------------------------

  const handleChange = (e) => {
    const {
      name,
      value,
      type,
      checked,
    } = e.target;
    setForm((prev) => {
      const updated = {
        ...prev,
        [name]:
          type === "checkbox"
            ? checked
            : type === "number"
              ? Number(value)
              : value,
      };
      // Auto calculate guests
      if (
        name === "adults" ||
        name === "children"
      ) {

        updated.numberOfGuests =
          Number(
            name === "adults"
              ? value
              : prev.adults
          )
          +
          Number(
            name === "children"
              ? value
              : prev.children
          );

      }
      return updated;
    });

  };

  //------------------------------------------------
  // Reset Modal
  //------------------------------------------------

  const resetForm = () => {
    setForm(initialState);
    setRooms([]);
    setPriceSummary(null);
  };

  //------------------------------------------------
  // Close Modal
  //------------------------------------------------

  const closeModal = () => {
    resetForm();
    toggle();
  };

  //------------------------------------------------
  // Load Room Types
  //------------------------------------------------

  const loadRoomTypes = async () => {
    try {
      const res = await axios.get("/manage/room-types");

      setRoomTypes(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Unable to load room types");
    }
  };

  //------------------------------------------------
  // Load Available Rooms
  //------------------------------------------------

  const loadRooms = async (roomTypeId) => {
    if (!roomTypeId) {
      setRooms([]);
      return;
    }

    try {
      setLoadingRooms(true);

      const res = await axios.get(
        `/manage/add-rooms?roomTypeId=${roomTypeId}&availableOnly=true`
      );

      setRooms(res.data || []);
    } catch (err) {
      console.error(err);

      setRooms([]);

      toast.error("Unable to load available rooms");
    } finally {
      setLoadingRooms(false);
    }
  };

  //------------------------------------------------
  // Calculate Booking Price
  //------------------------------------------------

  const calculatePrice = async () => {
    if (
      !form.roomTypeId ||
      !form.checkIn ||
      !form.checkOut
    ) {
      setPriceSummary(null);
      return;
    }

    try {
      const res = await axios.post(
        "/bookings/calculate-price",
        {
          roomTypeId: form.roomTypeId,
          checkIn: form.checkIn,
          checkOut: form.checkOut,
          adults: form.adults,
          children: form.children,
          extraBedRequired: form.extraBedRequired,
          extraBedCount: form.extraBedCount,
        }
      );

      setPriceSummary(res.data);
    } catch (err) {
      console.error(err);
      setPriceSummary(null);
    }
  };

  //------------------------------------------------
  // Load Room Types
  //------------------------------------------------

  useEffect(() => {
    if (isOpen) {
      loadRoomTypes();
    }
  }, [isOpen]);

  //------------------------------------------------
  // Load Rooms
  //------------------------------------------------

  useEffect(() => {
    if (form.roomTypeId) {
      loadRooms(form.roomTypeId);
    } else {
      setRooms([]);
    }
  }, [form.roomTypeId]);

  //------------------------------------------------
  // Auto Price Calculation
  //------------------------------------------------

  useEffect(() => {
    calculatePrice();
  }, [
    form.roomTypeId,
    form.checkIn,
    form.checkOut,
    form.adults,
    form.children,
    form.extraBedRequired,
    form.extraBedCount,
  ]);

  //------------------------------------------------
  // Create Reservation
  //------------------------------------------------

  const handleSubmit = async () => {
    try {
      setLoading(true);

      await axios.post("/bookings", {
        ...form,
        advanceAmount: form.advanceAmount,
        discountAmount: form.discountAmount,
      });

      toast.success("Reservation Created Successfully");

      if (onSuccess) {
        onSuccess();
      }

      closeModal();
    } catch (err) {
      console.error(err);

      toast.error(
        err.response?.data?.message ||
        "Unable to create reservation"
      );
    } finally {
      setLoading(false);
    }
  };
  return (
    <Modal
      isOpen={isOpen}
      toggle={closeModal}
      size="xl"
      centered
      backdrop="static"
    >
      <ModalHeader toggle={closeModal}>
        Create Reservation
      </ModalHeader>

      <ModalBody>

        {/* ============================================= */}
        {/* Guest Information */}
        {/* ============================================= */}

        <Card className="mb-4">

          <CardHeader>
            <h5 className="mb-0">
              Guest Information
            </h5>
          </CardHeader>

          <CardBody>

            <Row className="g-3">

              <Col md={4}>
                <Label>
                  Guest Name *
                </Label>

                <Input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                />
              </Col>

              <Col md={4}>
                <Label>
                  Mobile *
                </Label>

                <Input
                  name="mobile"
                  value={form.mobile}
                  onChange={handleChange}
                />
              </Col>

              <Col md={4}>
                <Label>
                  Email
                </Label>

                <Input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                />
              </Col>

              <Col md={6}>
                <Label>
                  Address
                </Label>

                <Input
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                />
              </Col>

              <Col md={3}>
                <Label>
                  Nationality
                </Label>

                <Input
                  name="nationality"
                  value={form.nationality}
                  onChange={handleChange}
                />
              </Col>

              <Col md={3}>
                <Label>
                  ID Proof Type
                </Label>

                <Input
                  type="select"
                  name="idProofType"
                  value={form.idProofType}
                  onChange={handleChange}
                >
                  <option value="">
                    Select
                  </option>

                  <option value="Aadhar">
                    Aadhar
                  </option>

                  <option value="PAN">
                    PAN
                  </option>

                  <option value="Passport">
                    Passport
                  </option>

                  <option value="Driving License">
                    Driving License
                  </option>
                </Input>
              </Col>

              <Col md={4}>
                <Label>
                  ID Proof Number
                </Label>

                <Input
                  name="idProofNumber"
                  value={form.idProofNumber}
                  onChange={handleChange}
                />
              </Col>

            </Row>

          </CardBody>

        </Card>

        {/* ============================================= */}
        {/* Stay Details */}
        {/* ============================================= */}

        <Card className="mb-4">

          <CardHeader>
            <h5 className="mb-0">
              Stay Details
            </h5>
          </CardHeader>

          <CardBody>

            <Row className="g-3">

              <Col md={3}>
                <Label>
                  Check In
                </Label>

                <Input
                  type="date"
                  name="checkIn"
                  value={form.checkIn}
                  onChange={handleChange}
                />
              </Col>

              <Col md={3}>
                <Label>
                  Check Out
                </Label>

                <Input
                  type="date"
                  name="checkOut"
                  value={form.checkOut}
                  onChange={handleChange}
                />
              </Col>

              <Col md={2}>
                <Label>
                  Adults
                </Label>

                <Input
                  type="number"
                  min={1}
                  name="adults"
                  value={form.adults}
                  onChange={handleChange}
                />
              </Col>

              <Col md={2}>
                <Label>
                  Children
                </Label>

                <Input
                  type="number"
                  min={0}
                  name="children"
                  value={form.children}
                  onChange={handleChange}
                />
              </Col>

              <Col md={2}>
                <Label>
                  Guests
                </Label>
                <Input
                  type="number"
                  value={
                    form.numberOfGuests
                  }
                  disabled
                  className="bg-light"
                />
              </Col>

            </Row>

          </CardBody>

        </Card>

        {/* ============================================= */}
        {/* Room Allocation */}
        {/* ============================================= */}

        <Card>

          <CardHeader>
            <h5 className="mb-0">
              Room Allocation
            </h5>
          </CardHeader>

          <CardBody>

            <Row className="g-3">

              <Col md={4}>

                <Label>
                  Room Type
                </Label>

                <Input
                  type="select"
                  name="roomTypeId"
                  value={form.roomTypeId}
                  onChange={handleChange}
                >
                  <option value="">
                    Select Room Type
                  </option>

                  {roomTypes.map((rt) => (
                    <option
                      key={rt._id}
                      value={rt._id}
                    >
                      {rt.name}
                    </option>
                  ))}

                </Input>

              </Col>

              <Col md={4}>

                <Label>
                  Available Room
                </Label>

                <Input
                  type="select"
                  name="assignedRoom"
                  value={form.assignedRoom}
                  onChange={handleChange}
                >
                  <option value="">
                    Select Room
                  </option>

                  {rooms.length === 0 ? (
                    <option value="">
                      No Rooms Available
                    </option>
                  ) : (
                    rooms.map((room) => (
                      <option
                        key={room._id}
                        value={room._id}
                      >
                        Room {room.roomNumber}
                        {room.building ? ` • ${room.building}` : ""}
                        {room.floor ? ` • Floor ${room.floor}` : ""}
                      </option>
                    ))
                  )}

                </Input>

              </Col>

              <Col md={2}>

                <Label>
                  Extra Bed
                </Label>

                <div className="mt-2">

                  <Input
                    type="checkbox"
                    name="extraBedRequired"
                    checked={form.extraBedRequired}
                    onChange={handleChange}
                  />

                </div>

              </Col>

              <Col md={2}>

                <Label>
                  Count
                </Label>

                <Input
                  type="number"
                  min={0}
                  name="extraBedCount"
                  disabled={!form.extraBedRequired}
                  value={form.extraBedCount}
                  onChange={handleChange}
                />

              </Col>

            </Row>

          </CardBody>

        </Card>
        {/* ============================================= */}
        {/* Booking Information */}
        {/* ============================================= */}

        <Card className="mt-4">

          <CardHeader>
            <h5 className="mb-0">
              Booking Information
            </h5>
          </CardHeader>

          <CardBody>

            <Row className="g-3">

              <Col md={4}>

                <Label>
                  Booking Source
                </Label>

                <Input
                  type="select"
                  name="source"
                  value={form.source}
                  onChange={handleChange}
                >
                  <option value="MANUAL">
                    Manual
                  </option>

                  <option value="WALKIN">
                    Walk-In
                  </option>

                  <option value="PHONE">
                    Phone
                  </option>

                  <option value="WEBSITE">
                    Website
                  </option>

                </Input>

              </Col>

              <Col md={4}>

                <Label>
                  Booking Channel
                </Label>

                <Input
                  type="select"
                  name="channel"
                  value={form.channel}
                  onChange={handleChange}
                >

                  <option value="DIRECT">
                    Direct
                  </option>

                  <option value="BOOKINGCOM">
                    Booking.com
                  </option>

                  <option value="AGODA">
                    Agoda
                  </option>

                  <option value="EXPEDIA">
                    Expedia
                  </option>

                  <option value="AIRBNB">
                    Airbnb
                  </option>

                </Input>

              </Col>

              <Col md={4}>

                <Label>
                  Payment Mode
                </Label>

                <Input
                  type="select"
                  name="paymentMode"
                  value={form.paymentMode}
                  onChange={handleChange}
                >

                  <option value="offline">
                    Offline
                  </option>

                  <option value="online">
                    Online
                  </option>

                </Input>

              </Col>

              <Col md={12}>

                <Label>
                  Special Request
                </Label>

                <Input
                  type="textarea"
                  rows={2}
                  name="specialRequest"
                  value={form.specialRequest}
                  onChange={handleChange}
                />

              </Col>

              <Col md={12}>

                <Label>
                  Internal Notes
                </Label>

                <Input
                  type="textarea"
                  rows={3}
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                />

              </Col>

            </Row>

          </CardBody>

        </Card>

        {/* ============================================= */}
        {/* Payment */}
        {/* ============================================= */}

        <Card className="mt-4">

          <CardHeader>

            <h5 className="mb-0">
              Advance Payment
            </h5>

          </CardHeader>

          <CardBody>

            <Row className="g-3">

              <Col md={6}>

                <Label>
                  Advance Amount
                </Label>

                <Input
                  type="number"
                  min={0}
                  name="advanceAmount"
                  value={form.advanceAmount}
                  onChange={handleChange}
                />

              </Col>

              <Col md={6}>

                <Label>
                  Discount
                </Label>

                <Input
                  type="number"
                  min={0}
                  name="discountAmount"
                  value={form.discountAmount}
                  onChange={handleChange}
                />

              </Col>

            </Row>

          </CardBody>

        </Card>

        {/* ============================================= */}
        {/* Booking Summary */}
        {/* ============================================= */}

        <Card className="mt-4 mb-3">

          <CardHeader>

            <h5 className="mb-0">
              Booking Summary
            </h5>

          </CardHeader>

          <CardBody>
            {!priceSummary ? (
  <div className="text-center py-4 text-muted">
    Select Room Type, Dates and Guests
    to calculate booking price.
  </div>
) : (
  <>
    <Row className="g-3">
      <Col md={3}>
        <h6 className="text-muted">Room Charge</h6>
        <h4 className="text-primary mb-0">
          ₹{Number(priceSummary.roomCharge || 0).toLocaleString()}
        </h4>
      </Col>

      <Col md={3}>
        <h6 className="text-muted">Nights</h6>
        <h4 className="mb-0">{priceSummary.nights}</h4>
      </Col>

      <Col md={3}>
        <h6 className="text-muted">Per Night</h6>
        <h4 className="mb-0">
          ₹{Number(priceSummary.ratePerNight || 0).toLocaleString()}
        </h4>
      </Col>

      <Col md={3}>
        <h6 className="text-muted">GST ({priceSummary.gstRate}%)</h6>
        <h4 className="mb-0">
          ₹{Number(priceSummary.taxAmount || 0).toLocaleString()}
        </h4>
      </Col>
    </Row>

    <hr />

    <div className="d-flex justify-content-between align-items-center">
      <h5 className="mb-0">Total Amount</h5>
      <h3 className="mb-0 text-success">
        ₹{Number(priceSummary.totalAmount || 0).toLocaleString()}
      </h3>
    </div>
  </>
)}
          </CardBody>

          <Row>
            <Col md={12} className="mt-2">
              <Input
                type="checkbox"
                checked={fullPayment}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setFullPayment(checked);

                  if (checked && priceSummary) {
                    setForm((prev) => ({
                      ...prev,
                      advanceAmount:
priceSummary.totalAmount,
                    }));
                  }
                }}
              />
              <Label className="ms-2">
                Full Payment Received
              </Label>
            </Col>
          </Row>

        </Card>
      </ModalBody>

      <ModalFooter>

        <Button
          color="secondary"
          onClick={closeModal}
          disabled={loading}
        >
          Cancel
        </Button>

        <Button
          color="primary"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner
                size="sm"
                className="me-2"
              />
              Creating Reservation...
            </>
          ) : (
            "Create Reservation"
          )}
        </Button>

      </ModalFooter>

    </Modal>
  );
};

export default CreateReservationModal;